import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import initialState from './initialState';
import {
  adaptCompanyPermissions,
  adaptPermissionGroupDetail,
} from './adapters';
import {
  createPermissionGroup,
  deletePermissionGroup,
  getCompanyPermissions,
  getPermissionGroup,
  getPermissionGroups,
  updatePermissionGroupName,
  setControlListingEditionActive,
  toggleAgentSupervision,
  getPortfolioManagers,
  addPortfolioManager,
  removePortfolioManager,
  setPortfolioManagerBranches,
  getUsersWithoutSupervision,
  addUserWithoutSupervision,
  removeUserWithoutSupervision,
  getSupervisionPolicy,
  updateSupervisionPolicy,
  getCompanyBranches,
  getAgentSupervisionConfig,
  saveAgentSupervisionRules,
  updateAgentSupervisionLock,
  getAgentSupervisionExemptions,
  addAgentSupervisionExemption,
  removeAgentSupervisionExemption,
  setExemptAdminsFromWarnings,
} from './services';
import { adaptAssigneeList } from './assignees';
import {
  applyPermissionEnabled,
  isSupervisionSystemUpdating,
  removeUpdatingSystem,
  toCanEditById,
  toEnabledById,
} from './utils';

// Every supervision system toggles through its own thunk, but they all track
// the same `supervisionSystems` bookkeeping.
const supervisionTogglePending = (state, action) => {
  const { id } = action.meta.arg;

  if (isSupervisionSystemUpdating(state.supervisionSystems.updatingIds, id)) {
    return;
  }

  state.supervisionSystems.updatingIds.push(id);
};

const supervisionToggleFulfilled = (state, action) => {
  const { id, enabled } = action.payload;

  state.supervisionSystems.enabledById[id] = enabled;
  state.supervisionSystems.updatingIds = removeUpdatingSystem(
    state.supervisionSystems.updatingIds,
    id
  );
  state.supervisionSystems.pendingChange = null;
};

// Every write to an assignee list answers with the whole list, so loading and
// editing land on the same reducer. The UI blocks the list while a write is in
// flight: one write at a time means responses can't arrive out of order and
// overwrite a newer list with an older one.
const addAssigneeListCases = (builder, listKey, get, writes) => {
  builder
    .addMatcher(isPending(get), (state) => {
      state[listKey].loading = true;
      state[listKey].error = null;
    })
    .addMatcher(isRejected(get), (state, action) => {
      state[listKey].loading = false;
      state[listKey].error =
        action.error?.message || `Failed to fetch ${listKey}`;
    })
    .addMatcher(isPending(...writes), (state) => {
      state[listKey].saving = true;
    })
    .addMatcher(isRejected(...writes), (state) => {
      state[listKey].saving = false;
    })
    .addMatcher(isFulfilled(get, ...writes), (state, action) => {
      state[listKey].items = adaptAssigneeList(action.payload);
      state[listKey].loading = false;
      state[listKey].loaded = true;
      state[listKey].error = null;
      state[listKey].saving = false;
    });
};

// The resources each dashboard tab fetches on mount. Global permissions keeps
// its flags at the root of the slice, so it is handled apart.
const TAB_RESOURCES = {
  permissionGroups: ['permissionGroups'],
  supervisionSystems: [
    'supervisionSystems',
    'agentSupervisionConfig',
    'agentSupervisionExemptions',
    'portfolioManagers',
    'usersWithoutSupervision',
    'supervisionPolicy',
    'companyBranches',
  ],
};

const findAgentSupervisionRule = (rules, ruleAction) =>
  rules.find(({ action }) => action === ruleAction);

const replaceAgentSupervisionRule = (state, rule) => {
  const { rules } = state.agentSupervisionConfig;
  const index = rules.findIndex(({ action }) => action === rule.action);

  if (index !== -1) rules[index] = rule;
};

// Whether a sent rule is still as it was sent, i.e. not edited again while
// its save was in flight.
const isUnchangedSinceSent = (state, sent) =>
  isEqual(
    findAgentSupervisionRule(state.agentSupervisionConfig.rules, sent.action),
    sent
  );

// A write may answer with an empty body, which ApiService turns into `{}`.
const adaptAgentSupervisionExemptions = (response) => {
  const { admins, exempt_admins_from_warnings: exemptAdmins } = response || {};

  return {
    assignees: adaptAssigneeList(response),
    admins: admins || [],
    exempt_admins_from_warnings: Boolean(exemptAdmins),
  };
};

const supervisionToggleRejected = (state, action) => {
  state.supervisionSystems.updatingIds = removeUpdatingSystem(
    state.supervisionSystems.updatingIds,
    action.meta.arg.id
  );
  state.supervisionSystems.pendingChange = null;
};

export const CompanyPermissionsSlice = createSlice({
  name: 'CompanyPermissions',
  initialState,
  reducers: {
    setPermissionEnabled(state, action) {
      const { permissionId, enabled } = action.payload;

      applyPermissionEnabled(state.permission_groups, permissionId, enabled);
    },
    setPermissionGroupPermissionEnabled(state, action) {
      const { permissionId, enabled } = action.payload;

      if (!state.permissionGroupDetail.item) return;

      applyPermissionEnabled(
        state.permissionGroupDetail.item.permission_groups,
        permissionId,
        enabled
      );
    },
    requestSupervisionToggle(state, action) {
      const { id, enabled, disableWork = null } = action.payload;

      if (
        isSupervisionSystemUpdating(state.supervisionSystems.updatingIds, id)
      ) {
        return;
      }

      state.supervisionSystems.pendingChange = { id, enabled, disableWork };
    },
    setPendingDisableWork(state, action) {
      if (!state.supervisionSystems.pendingChange) return;

      state.supervisionSystems.pendingChange.disableWork = action.payload;
    },
    cancelSupervisionToggle(state) {
      state.supervisionSystems.pendingChange = null;
    },
    setSupervisionSystemsLoading(state) {
      state.supervisionSystems.loading = true;
      state.supervisionSystems.error = null;
    },
    setSupervisionSystemsError(state, action) {
      state.supervisionSystems.loading = false;
      state.supervisionSystems.error = action.payload;
    },
    // `{ supervision_systems: [{ id, enabled, can_edit }] }`, merged from both
    // endpoints at the dispatch site. A system whose fetch failed is not in it.
    setSupervisionSystems(state, action) {
      state.supervisionSystems.enabledById = toEnabledById(
        action.payload?.supervision_systems
      );
      state.supervisionSystems.canEditById = toCanEditById(
        action.payload?.supervision_systems
      );
      state.supervisionSystems.loading = false;
      state.supervisionSystems.error = null;
      state.supervisionSystems.loaded = true;
    },
    // Shown right away and saved later by `saveAgentSupervisionRules`. The
    // first edit keeps the saved rule to roll back to; editing it back to that
    // leaves nothing to save.
    stageAgentSupervisionRule(state, action) {
      const rule = action.payload;
      const { rules, pendingRules } = state.agentSupervisionConfig;

      if (!(rule.action in pendingRules)) {
        pendingRules[rule.action] = findAgentSupervisionRule(
          rules,
          rule.action
        );
      }

      replaceAgentSupervisionRule(state, rule);

      if (isEqual(rule, pendingRules[rule.action])) {
        delete pendingRules[rule.action];
      }
    },
    // Leaving a tab marks its data as not loaded, so the next visit fetches it
    // again instead of showing what was loaded before.
    setPermissionsTabIdle(state, action) {
      const tab = action.payload;

      if (tab === 'globalPermissions') {
        state.loaded = false;
        // This tab shows its skeleton from `loading`, not from `!loaded`.
        state.loading = true;
        state.error = null;
        // Discards whatever `getCompanyPermissions` call is still in flight:
        // its eventual fulfilled/rejected won't match this and will be ignored.
        state.fetchRequestId = null;
        return;
      }

      TAB_RESOURCES[tab].forEach((key) => {
        state[key].loaded = false;
        state[key].loading = false;
        state[key].error = null;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyPermissions.pending, (state, action) => {
        state.loading = true;
        state.fetchInFlight = true;
        state.error = null;
        state.fetchRequestId = action.meta.requestId;
      })
      .addCase(getCompanyPermissions.fulfilled, (state, action) => {
        state.fetchInFlight = false;

        // The tab was left (and the catalog reset) while this was in flight:
        // applying it now would resurrect data that should stay hidden.
        if (action.meta.requestId !== state.fetchRequestId) return;

        state.permission_groups = adaptCompanyPermissions(
          action.payload
        ).permission_groups;
        state.loading = false;
        state.loaded = true;
        state.error = null;
      })
      .addCase(getCompanyPermissions.rejected, (state, action) => {
        state.fetchInFlight = false;

        if (action.meta.requestId !== state.fetchRequestId) return;

        state.loading = false;
        state.error =
          action.error?.message || 'Failed to fetch company permissions';
      })
      .addCase(getPermissionGroups.pending, (state) => {
        state.permissionGroups.loading = true;
        state.permissionGroups.error = null;
      })
      .addCase(getPermissionGroups.fulfilled, (state, action) => {
        state.permissionGroups.items = action.payload?.results || [];
        state.permissionGroups.loading = false;
        state.permissionGroups.loaded = true;
        state.permissionGroups.error = null;
      })
      .addCase(getPermissionGroups.rejected, (state, action) => {
        state.permissionGroups.loading = false;
        state.permissionGroups.error =
          action.error?.message || 'Failed to fetch permission groups';
      })
      .addCase(createPermissionGroup.pending, (state) => {
        state.permissionGroups.creating = true;
      })
      .addCase(createPermissionGroup.fulfilled, (state, action) => {
        if (action.payload) {
          state.permissionGroups.items.push(action.payload);
        }

        state.permissionGroups.creating = false;
        state.permissionGroups.loaded = true;
        state.permissionGroups.error = null;
      })
      .addCase(createPermissionGroup.rejected, (state) => {
        state.permissionGroups.creating = false;
      })
      .addCase(getPermissionGroup.pending, (state, action) => {
        state.permissionGroupDetail.loading = true;
        state.permissionGroupDetail.error = null;
        state.permissionGroupDetail.requestedId = String(action.meta.arg);
      })
      .addCase(getPermissionGroup.fulfilled, (state, action) => {
        // Ignore responses for a group the user has since navigated away
        // from — otherwise an out-of-order response can show the wrong
        // group or strand the page on the loading skeleton.
        if (
          String(action.meta.arg) !== state.permissionGroupDetail.requestedId
        ) {
          return;
        }

        state.permissionGroupDetail.item = adaptPermissionGroupDetail(
          action.payload
        );
        state.permissionGroupDetail.loading = false;
        state.permissionGroupDetail.error = null;
      })
      .addCase(getPermissionGroup.rejected, (state, action) => {
        if (
          String(action.meta.arg) !== state.permissionGroupDetail.requestedId
        ) {
          return;
        }

        state.permissionGroupDetail.loading = false;
        state.permissionGroupDetail.error =
          action.error?.message || 'Failed to fetch permission group';
      })
      .addCase(updatePermissionGroupName.pending, (state) => {
        state.permissionGroupDetail.updatingName = true;
      })
      .addCase(updatePermissionGroupName.fulfilled, (state, action) => {
        const { id, name } = action.payload;
        const group = state.permissionGroups.items.find(
          (item) => String(item.id) === String(id)
        );

        if (group) group.name = name;

        if (
          state.permissionGroupDetail.item &&
          String(state.permissionGroupDetail.item.id) === String(id)
        ) {
          state.permissionGroupDetail.item.name = name;
        }
        state.permissionGroupDetail.updatingName = false;
      })
      .addCase(updatePermissionGroupName.rejected, (state) => {
        state.permissionGroupDetail.updatingName = false;
      })
      .addCase(deletePermissionGroup.pending, (state) => {
        state.permissionGroupDetail.deleting = true;
      })
      .addCase(deletePermissionGroup.fulfilled, (state, action) => {
        const { id } = action.payload;

        state.permissionGroups.items = state.permissionGroups.items.filter(
          (item) => String(item.id) !== String(id)
        );

        if (
          state.permissionGroupDetail.item &&
          String(state.permissionGroupDetail.item.id) === String(id)
        ) {
          state.permissionGroupDetail.item = null;
        }
        state.permissionGroupDetail.deleting = false;
      })
      .addCase(deletePermissionGroup.rejected, (state) => {
        state.permissionGroupDetail.deleting = false;
      })
      .addCase(toggleAgentSupervision.pending, supervisionTogglePending)
      .addCase(toggleAgentSupervision.fulfilled, supervisionToggleFulfilled)
      .addCase(toggleAgentSupervision.rejected, supervisionToggleRejected)
      .addCase(setControlListingEditionActive.pending, supervisionTogglePending)
      .addCase(
        setControlListingEditionActive.fulfilled,
        supervisionToggleFulfilled
      )
      .addCase(
        setControlListingEditionActive.rejected,
        supervisionToggleRejected
      )
      .addCase(getSupervisionPolicy.pending, (state) => {
        state.supervisionPolicy.loading = true;
        state.supervisionPolicy.error = null;
      })
      .addCase(getSupervisionPolicy.fulfilled, (state, action) => {
        state.supervisionPolicy.value = action.payload;
        state.supervisionPolicy.loading = false;
        state.supervisionPolicy.loaded = true;
      })
      .addCase(getSupervisionPolicy.rejected, (state, action) => {
        state.supervisionPolicy.loading = false;
        state.supervisionPolicy.error =
          action.error?.message || 'Failed to fetch supervision policy';
      })
      .addCase(updateSupervisionPolicy.pending, (state) => {
        state.supervisionPolicy.saving = true;
      })
      .addCase(updateSupervisionPolicy.fulfilled, (state, action) => {
        state.supervisionPolicy.value = action.payload;
        state.supervisionPolicy.saving = false;
      })
      .addCase(updateSupervisionPolicy.rejected, (state) => {
        state.supervisionPolicy.saving = false;
      })
      .addCase(getAgentSupervisionConfig.pending, (state) => {
        state.agentSupervisionConfig.loading = true;
        state.agentSupervisionConfig.error = null;
      })
      .addCase(getAgentSupervisionConfig.fulfilled, (state, action) => {
        state.agentSupervisionConfig.lock = action.payload?.lock ?? null;
        state.agentSupervisionConfig.rules = action.payload?.rules || [];
        state.agentSupervisionConfig.loading = false;
        state.agentSupervisionConfig.loaded = true;
      })
      .addCase(getAgentSupervisionConfig.rejected, (state, action) => {
        state.agentSupervisionConfig.loading = false;
        state.agentSupervisionConfig.error =
          action.error?.message || 'Failed to fetch agent supervision config';
      })
      .addCase(saveAgentSupervisionRules.pending, (state) => {
        const { rules, pendingRules } = state.agentSupervisionConfig;

        state.agentSupervisionConfig.savingRules = rules.filter(
          ({ action }) => action in pendingRules
        );
      })
      // A rule edited again while in flight stays pending, now against what
      // the backend stored, unless it was edited back to that.
      .addCase(saveAgentSupervisionRules.fulfilled, (state, action) => {
        const config = state.agentSupervisionConfig;

        config.savingRules.forEach((sent) => {
          const saved =
            findAgentSupervisionRule(action.payload || [], sent.action) || sent;

          if (isUnchangedSinceSent(state, sent)) {
            replaceAgentSupervisionRule(state, saved);
          }

          if (isUnchangedSinceSent(state, saved)) {
            delete config.pendingRules[sent.action];
          } else {
            config.pendingRules[sent.action] = saved;
          }
        });
        config.savingRules = [];
      })
      // Only rolls back the rules not edited again while in flight: those
      // stay pending for the next save.
      .addCase(saveAgentSupervisionRules.rejected, (state) => {
        const config = state.agentSupervisionConfig;

        config.savingRules.forEach((sent) => {
          if (!isUnchangedSinceSent(state, sent)) return;

          replaceAgentSupervisionRule(state, config.pendingRules[sent.action]);
          delete config.pendingRules[sent.action];
        });
        config.savingRules = [];
      })
      // Shown right away so a rule save sent meanwhile carries the new lock.
      .addCase(updateAgentSupervisionLock.pending, (state, action) => {
        const config = state.agentSupervisionConfig;

        config.savedLock = config.lock;
        config.lock = action.meta.arg;
        config.savingLock = true;
      })
      .addCase(updateAgentSupervisionLock.fulfilled, (state, action) => {
        state.agentSupervisionConfig.lock = action.payload;
        state.agentSupervisionConfig.savedLock = null;
        state.agentSupervisionConfig.savingLock = false;
      })
      .addCase(updateAgentSupervisionLock.rejected, (state) => {
        const config = state.agentSupervisionConfig;

        config.lock = config.savedLock;
        config.savedLock = null;
        config.savingLock = false;
      })
      .addCase(getCompanyBranches.pending, (state) => {
        state.companyBranches.loading = true;
        state.companyBranches.error = null;
      })
      .addCase(getCompanyBranches.fulfilled, (state, action) => {
        state.companyBranches.items = action.payload;
        state.companyBranches.loading = false;
        state.companyBranches.loaded = true;
      })
      .addCase(getCompanyBranches.rejected, (state, action) => {
        state.companyBranches.loading = false;
        state.companyBranches.error =
          action.error?.message || 'Failed to fetch company branches';
      });

    addAssigneeListCases(builder, 'portfolioManagers', getPortfolioManagers, [
      addPortfolioManager,
      removePortfolioManager,
      setPortfolioManagerBranches,
    ]);
    addAssigneeListCases(
      builder,
      'usersWithoutSupervision',
      getUsersWithoutSupervision,
      [addUserWithoutSupervision, removeUserWithoutSupervision]
    );

    // Same one-write-at-a-time bookkeeping as the assignee lists, but the
    // response is kept as the backend sends it.
    const exemptionWrites = [
      addAgentSupervisionExemption,
      removeAgentSupervisionExemption,
      setExemptAdminsFromWarnings,
    ];

    builder
      .addMatcher(isPending(getAgentSupervisionExemptions), (state) => {
        state.agentSupervisionExemptions.loading = true;
        state.agentSupervisionExemptions.error = null;
      })
      .addMatcher(
        isRejected(getAgentSupervisionExemptions),
        (state, action) => {
          state.agentSupervisionExemptions.loading = false;
          state.agentSupervisionExemptions.error =
            action.error?.message ||
            'Failed to fetch agent supervision exemptions';
        }
      )
      .addMatcher(isPending(...exemptionWrites), (state) => {
        state.agentSupervisionExemptions.saving = true;
      })
      .addMatcher(isRejected(...exemptionWrites), (state) => {
        state.agentSupervisionExemptions.saving = false;
      })
      // Shown right away; a failed save flips it back.
      .addMatcher(isPending(setExemptAdminsFromWarnings), (state, action) => {
        state.agentSupervisionExemptions.data.exempt_admins_from_warnings =
          action.meta.arg;
      })
      .addMatcher(isRejected(setExemptAdminsFromWarnings), (state, action) => {
        state.agentSupervisionExemptions.data.exempt_admins_from_warnings =
          !action.meta.arg;
      })
      .addMatcher(
        isFulfilled(getAgentSupervisionExemptions, ...exemptionWrites),
        (state, action) => {
          state.agentSupervisionExemptions.data =
            adaptAgentSupervisionExemptions(action.payload);
          state.agentSupervisionExemptions.loading = false;
          state.agentSupervisionExemptions.loaded = true;
          state.agentSupervisionExemptions.error = null;
          state.agentSupervisionExemptions.saving = false;
        }
      );
  },
});

export const {
  setPermissionEnabled,
  setPermissionGroupPermissionEnabled,
  requestSupervisionToggle,
  setPendingDisableWork,
  cancelSupervisionToggle,
  setSupervisionSystemsLoading,
  setSupervisionSystemsError,
  setSupervisionSystems,
  setPermissionsTabIdle,
  stageAgentSupervisionRule,
} = CompanyPermissionsSlice.actions;

export const selectCompanyPermissions = (state) =>
  state.CompanyPermissionsSlice;

export default CompanyPermissionsSlice.reducer;
